import { ActionIcon, Box, Flex, Group, Modal, Tooltip } from "@mantine/core";
import { IAzureBlobFile } from "@schemas/azureSchema";
import { IconDownload, IconExternalLink, IconTrash, IconX } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React from "react";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";
import { formatShortDate } from "../../app/routes/admin/selectors";
import { Checkbox, ConfirmDialog } from ".././";

const FileTable: React.FC<{
  files: IAzureBlobFile[];
  downloadFile: (fileId: string, fileName: string) => void;
  deleteFile: (fileId: string) => void;
  showDocxImageTag?: boolean;
  folderPath?: string;
  allowToggleItems?: boolean;
  onToggleItem?: (fileId: string) => void;
}> = ({
  files,
  downloadFile,
  deleteFile,
  showDocxImageTag = false,
  folderPath,
  allowToggleItems = false,
  onToggleItem,
}) => {
  const [openImageModal, setOpenImageModal] = React.useState(false);
  const [activeImg, setActiveImg] = React.useState("");
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [zoom, setZoom] = React.useState(1);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = React.useState(false);
  const viewerRef = React.useRef<HTMLDivElement | null>(null);
  const imageNaturalRef = React.useRef({ width: 0, height: 0 });
  const panStartRef = React.useRef<{ x: number; y: number; originX: number; originY: number } | null>(null);

  const onClickImg = (filename: string) => {
    setActiveImg(filename);
    setIsFullscreen(true);
    setOpenImageModal(true);
  };

  const resetViewer = React.useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsPanning(false);
    panStartRef.current = null;
  }, []);

  const closeImageModal = React.useCallback(() => {
    setOpenImageModal(false);
    setIsFullscreen(false);
    resetViewer();
  }, [resetViewer]);

  const clampPan = React.useCallback(
    (nextPan: { x: number; y: number }, nextZoom = zoom) => {
      const viewer = viewerRef.current;
      const natural = imageNaturalRef.current;
      if (!viewer || natural.width === 0 || natural.height === 0) {
        return { x: 0, y: 0 };
      }

      const viewW = viewer.clientWidth;
      const viewH = viewer.clientHeight;
      const fitScale = Math.min(viewW / natural.width, viewH / natural.height);
      const baseW = natural.width * fitScale;
      const baseH = natural.height * fitScale;
      const scaledW = baseW * nextZoom;
      const scaledH = baseH * nextZoom;
      const maxX = Math.max(0, (scaledW - viewW) / 2);
      const maxY = Math.max(0, (scaledH - viewH) / 2);

      return {
        x: Math.min(maxX, Math.max(-maxX, nextPan.x)),
        y: Math.min(maxY, Math.max(-maxY, nextPan.y)),
      };
    },
    [zoom],
  );

  const applyZoom = React.useCallback(
    (targetZoom: number) => {
      const nextZoom = Math.min(6, Math.max(1, Number(targetZoom.toFixed(2))));
      setZoom(nextZoom);
      setPan((currentPan) => (nextZoom === 1 ? { x: 0, y: 0 } : clampPan(currentPan, nextZoom)));
    },
    [clampPan],
  );

  const handleViewerWheel = React.useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (!isFullscreen) {
        return;
      }
      event.preventDefault();
      applyZoom(zoom + (event.deltaY < 0 ? 0.2 : -0.2));
    },
    [applyZoom, isFullscreen, zoom],
  );

  const handlePanStart = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isFullscreen || zoom <= 1) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsPanning(true);
      panStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        originX: pan.x,
        originY: pan.y,
      };
    },
    [isFullscreen, pan.x, pan.y, zoom],
  );

  const handlePanMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const start = panStartRef.current;
      if (!isPanning || start == null) {
        return;
      }
      setPan(
        clampPan({
          x: start.originX + (event.clientX - start.x),
          y: start.originY + (event.clientY - start.y),
        }),
      );
    },
    [clampPan, isPanning],
  );

  const handlePanEnd = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsPanning(false);
    panStartRef.current = null;
  }, []);

  React.useEffect(() => {
    if (!openImageModal || !isFullscreen) {
      return;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeImageModal();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [closeImageModal, isFullscreen, openImageModal]);

  function formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  }

  const columns: ColDef<IAzureBlobFile>[] = [
    {
      headerName: "ID",
      field: "blobName",
      hide: true,
    },
    {
      headerName: "",
      suppressHeaderMenuButton: true,
      sortable: false,
      width: 80,
      cellClass: "px-1",
      cellRenderer: (params) => {
        return (
          <div className="d-flex">
            <Tooltip label="Last ned" withArrow position="top">
              <ActionIcon
                variant="subtle"
                color="blue"
                size="sm"
                onClick={() => downloadFile(params.data?.blobName, params.data?.fileName)}
                aria-label="Last ned fil"
              >
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
            <ConfirmDialog
              title="Slette fil?"
              message="Er du sikker på at du vil slette filen?"
              confirmCb={() => deleteFile(params.data?.blobName)}
            >
              <Tooltip label="Slett" withArrow position="top">
                <ActionIcon variant="subtle" color="red" size="sm" aria-label="Slett fil">
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </ConfirmDialog>
          </div>
        );
      },
    },
    {
      headerName: "Filnavn",
      field: "fileName",
      cellRenderer: (params) => {
        return <div className="ag-cell-text-truncate">{params.value}</div>;
      },
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: "Tagg for docxtemplater",
      field: showDocxImageTag === false ? "blobName" : "fileName", // MÅ ENDRES
      hide: showDocxImageTag === false,
      cellRenderer: (params) => (params.value != null ? `{%${params.value}}` : ""),
    },
    {
      headerName: "Opprettet",
      field: "uploadedAt",
      initialSort: "desc",
      cellRenderer: (params) => formatShortDate(params.value),
      width: 130,
    },
    {
      headerName: "Opplastet av",
      field: "uploadedBy",
    },
    {
      headerName: "Størrelse",
      field: "size",
      cellRenderer: (params) => {
        const formattedSize = formatFileSize(params.value);
        return <div className="ag-cell-text-truncate">{formattedSize}</div>;
      },
      width: 100,
    },
    {
      headerName: "Forhåndsvisning",
      sortable: false,
      cellClass: "d-flex justify-content-center",
      suppressHeaderMenuButton: true,
      cellRenderer: (params) => {
        const isPdf = params.data?.fileName.toLowerCase().endsWith(".pdf");
        return (
          <Flex>
            {isPdf ? (
              <a
                style={{ textDecoration: "none", color: "var(--mantine-color-blue-4)" }}
                href={`/api/pdf/${params.data?.blobName}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Flex gap="xs" align="center" justify="center" h="100%">
                  <IconExternalLink size={24} />
                </Flex>
              </a>
            ) : (
              <img
                className="pointer"
                src={`/api/image/${params.data?.blobName}`}
                alt=""
                style={{ maxWidth: "100px", maxHeight: "60px", objectFit: "contain" }}
                onClick={() => onClickImg(params.data?.blobName)}
              />
            )}
          </Flex>
        );
      },
    },
    {
      headerName: "",
      hide: allowToggleItems !== true,
      suppressHeaderMenuButton: true,
      cellRenderer: (params) => {
        return (
          <Checkbox
            isChecked={!!params}
            setChecked={() => {
              onToggleItem(params.data?._id);
            }}
            disabled={false}
          />
        );
      },
    },
  ];

  return (
    <div className="ag-theme-quartz h-100 w-100">
      <AgGridReact
        domLayout={"autoHeight"}
        rowData={files}
        defaultColDef={{
          suppressHeaderMenuButton: true,
        }}
        rowHeight={45}
        columnDefs={columns}
        suppressClickEdit={true}
        rowSelection={{ mode: "singleRow", checkboxes: false }}
        animateRows={true}
        suppressContextMenu={true}
        onGridReady={(e) => {
          e.api.autoSizeColumns(["createdBy.name"]);
        }}
        localeText={AG_GRID_LOCALE_NOR}
      />
      {openImageModal === true && (
        <Modal
          opened={openImageModal}
          onClose={closeImageModal}
          title={files?.find((file) => file.blobName === activeImg)?.fileName}
          size="60vw"
          yOffset={isFullscreen ? 0 : 80}
          withCloseButton={false}
          fullScreen={isFullscreen}
          closeOnEscape={!isFullscreen}
          overlayProps={{ blur: 2 }}
          keepMounted={false}
          zIndex={1200}
          styles={{
            body: {
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              height: isFullscreen ? "calc(100vh - 4rem)" : "auto",
            },
            content: { maxWidth: isFullscreen ? undefined : "1000px" },
          }}
        >
          <Group justify="flex-end" gap="xs" mb="xs">
            {isFullscreen && (
              <>
                <ActionIcon variant="subtle" color="gray" onClick={() => applyZoom(zoom - 0.2)} aria-label="Zoom ut">
                  <i className="fa fa-search-minus" />
                </ActionIcon>
                <ActionIcon variant="subtle" color="gray" onClick={() => applyZoom(zoom + 0.2)} aria-label="Zoom inn">
                  <i className="fa fa-search-plus" />
                </ActionIcon>
                <ActionIcon variant="subtle" color="gray" onClick={resetViewer} aria-label="Nullstill zoom">
                  <i className="fa fa-refresh" />
                </ActionIcon>
              </>
            )}
            <ActionIcon variant="subtle" color="gray" onClick={closeImageModal} aria-label="Lukk bildevisning">
              <IconX size={16} />
            </ActionIcon>
          </Group>
          <Box
            ref={viewerRef}
            className="gray-100"
            style={{
              borderRadius: 6,
              minHeight: 0,
              overflow: "hidden",
              flex: isFullscreen ? 1 : undefined,
              height: isFullscreen ? "100%" : "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isFullscreen && zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default",
              touchAction: "none",
            }}
            onWheel={handleViewerWheel}
            onPointerDown={handlePanStart}
            onPointerMove={handlePanMove}
            onPointerUp={handlePanEnd}
            onPointerCancel={handlePanEnd}
          >
            <img
              src={`/api/image/${activeImg}`}
              alt=""
              draggable={false}
              onLoad={(event) => {
                imageNaturalRef.current = {
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                };
                setPan((currentPan) => clampPan(currentPan, zoom));
              }}
              style={{
                width: isFullscreen ? "auto" : "100%",
                maxWidth: "100%",
                maxHeight: isFullscreen ? "100%" : "80vh",
                height: "auto",
                transform: isFullscreen ? `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` : undefined,
                transformOrigin: "center center",
                transition: isPanning ? "none" : "transform 120ms ease-out",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default FileTable;
